import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type HubSectionProps = {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
};

/** Tab-level section heading used inside resource hub pages. */
export function HubSection({ title, description, actions, children, className }: HubSectionProps) {
  return (
    <section className={cn('space-y-4', className)}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-base font-semibold tracking-tight">{title}</h2>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

export type HubCardProps = {
  children: ReactNode;
  className?: string;
};

/** Bordered content card for hub forms and detail blocks. */
export function HubCard({ children, className }: HubCardProps) {
  return (
    <div className={cn('rounded-lg border border-border bg-card p-4 md:p-5', className)}>
      {children}
    </div>
  );
}

export function FieldHint({ children }: { children: ReactNode }) {
  return <p className="text-xs text-muted-foreground">{children}</p>;
}

export type HubContentWidth = 'md' | 'lg' | 'xl';

const WIDTH_CLASS: Record<HubContentWidth, string> = {
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
};

export type HubContentProps = {
  children: ReactNode;
  width?: HubContentWidth;
  className?: string;
};

/** Constrains resource hub pages to a readable width. */
export function HubContent({ children, width = 'lg', className }: HubContentProps) {
  return (
    <div className={cn('mx-auto w-full space-y-5', WIDTH_CLASS[width], className)}>{children}</div>
  );
}
